import React, { createContext, useContext } from 'react';
import { useFilterActions } from './ActionsProvider';
import { useFilterState } from './StateProvider';
import { fromSummaryDiffToChanges, useDiff } from '../../../Shared/summaryDiff';
import { useTransportTypeProduct } from '@components/Filters/Components/TransportTypeFilter/Provider';

const AppliedFilterContext = createContext(false);
const UnsuitedFilterContext = createContext(false);
const FilterSummaryContext = createContext({});
const FilterChangesContext = createContext([]);

const noCarrierFilterSummary = {
  summary: [],
  isApplied: false,
  isUnsuited: false,
};

const calculateLegSummary = (carriers, product) => {
  const carriersByType =
    product !== ''
      ? carriers.filter(({ transportType }) => transportType === product)
      : carriers;
  const checkedCarriers = carriers.filter(({ checked }) => checked);
  const checkedCarriersByType =
    product !== ''
      ? checkedCarriers.filter(({ transportType }) => transportType === product)
      : checkedCarriers;
  const isApplied = checkedCarriersByType.length < carriersByType.length;
  const isUnsuited = checkedCarriersByType.length === 0;
  return {
    isApplied,
    isUnsuited,
    ...(isApplied &&
      !isUnsuited && {
        carrierName: checkedCarriersByType[0].name,
        count: checkedCarriersByType.length - 1,
      }),
  };
};

const createFilterSummary = (carriers, product) => {
  if (!carriers || carriers.length === 0) {
    return noCarrierFilterSummary;
  }
  const summary = [];
  let isApplied = false;
  let isUnsuited = false;

  carriers.forEach(({ carriers }) => {
    const legSummary = calculateLegSummary(carriers, product);
    isApplied = isApplied || legSummary.isApplied;
    isUnsuited = isUnsuited || legSummary.isUnsuited;
    summary.push(legSummary);
  });

  return {
    summary,
    isApplied,
    isUnsuited,
  };
};

const FilterChangesProvider = ({ summary, children }) => {
  const { previous, next } = useDiff(summary);
  const { selectLegAll } = useFilterActions();
  const changes = fromSummaryDiffToChanges(
    previous,
    next,
    'airline',
    selectLegAll,
    'default'
  );
  return (
    <FilterChangesContext.Provider value={changes}>
      {children}
    </FilterChangesContext.Provider>
  );
};

const FilterSummaryProvider = ({ children }) => {
  const carriers = useFilterState();
  const { product } = useTransportTypeProduct();
  const { summary, isApplied, isUnsuited } = createFilterSummary(
    carriers,
    product
  );
  return (
    <FilterSummaryContext.Provider value={summary}>
      <FilterChangesProvider summary={summary}>
        <AppliedFilterContext.Provider value={isApplied}>
          <UnsuitedFilterContext.Provider value={isUnsuited}>
            {children}
          </UnsuitedFilterContext.Provider>
        </AppliedFilterContext.Provider>
      </FilterChangesProvider>
    </FilterSummaryContext.Provider>
  );
};

export const useFilterSummary = () => useContext(FilterSummaryContext);
export const useFilterChanges = () => useContext(FilterChangesContext);
export const useAppliedFilter = () => useContext(AppliedFilterContext);
export const useUnsuitedFilter = () => useContext(UnsuitedFilterContext);

export default FilterSummaryProvider;
